<script setup lang="ts">
import { computed, ref } from 'vue'
import { CloseOutlined } from '@ant-design/icons-vue'
import { formatRelativeTime } from '@/components/ExecutionHistory/services/formatRelativeTime'
import { mapUserMetrics, mapTenantMetrics, mapSubscriptionMetrics } from '@/components/ExecutionHistory/models/MetricDisplayItem'
import type { MetricSummary } from '@/types'

const props = defineProps<{
  open: boolean
  metrics: MetricSummary | null
  isLoading: boolean
  hasError: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const activeTab = ref('user')

const relativeTime = computed(() => {
  if (!props.metrics?.lastFetch) return ''
  return formatRelativeTime(props.metrics.lastFetch)
})

const userMetrics = computed(() => {
  if (!props.metrics?.user) return []
  return mapUserMetrics(props.metrics.user)
})

const tenantMetrics = computed(() => {
  if (!props.metrics?.tenant) return []
  return mapTenantMetrics(props.metrics.tenant)
})

const hasSubscription = computed(() => {
  if (!props.metrics?.subscription) return false
  return Object.keys(props.metrics.subscription).length > 0
})

const subscriptionEntries = computed(() => {
  if (!hasSubscription.value) return []
  return mapSubscriptionMetrics(props.metrics!.subscription)
})

const hasDiagnostics = computed(() => {
  if (!props.metrics?.diagnostics) return false
  return Object.keys(props.metrics.diagnostics).length > 0
})

const diagnosticsEntries = computed(() => {
  if (!hasDiagnostics.value) return []
  return Object.entries(props.metrics!.diagnostics)
})

const hasMetrics = computed(() => {
  if (!props.metrics) return false
  return !!(props.metrics.user || props.metrics.tenant)
})

const maxRowCount = computed(() => {
  const counts = [userMetrics.value.length, tenantMetrics.value.length]
  if (hasSubscription.value) counts.push(subscriptionEntries.value.length)
  if (hasDiagnostics.value) counts.push(diagnosticsEntries.value.length)
  return Math.max(...counts, 0)
})

const userPadding = computed(() => maxRowCount.value - userMetrics.value.length)
const tenantPadding = computed(() => maxRowCount.value - tenantMetrics.value.length)
const subscriptionPadding = computed(() => maxRowCount.value - subscriptionEntries.value.length)
const diagnosticsPadding = computed(() => maxRowCount.value - diagnosticsEntries.value.length)

function handleClose() {
  emit('close')
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    emit('close')
  }
}
</script>

<template>
  <Transition name="metrics-panel">
    <div
      v-if="open"
      class="metrics-floating-panel"
      @keydown="handleKeydown"
      tabindex="-1"
    >
      <!-- Header -->
      <div class="metrics-floating-panel__header">
        <div class="metrics-floating-panel__title-row">
          <h3 class="metrics-floating-panel__title">Developer Metrics</h3>
          <button
            class="metrics-floating-panel__close-btn"
            aria-label="Close developer metrics panel"
            @click="handleClose"
          >
            <CloseOutlined />
          </button>
        </div>
        <div v-if="relativeTime" class="metrics-floating-panel__freshness">
          Last updated: {{ relativeTime }}
        </div>
      </div>

      <!-- Error Banner -->
      <div v-if="hasError" class="metrics-floating-panel__error-banner">
        <span>⚠ Failed to load metrics</span>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="metrics-floating-panel__body metrics-floating-panel__loading">
        <a-spin size="small" />
        <span class="metrics-floating-panel__loading-text">Loading metrics…</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasMetrics" class="metrics-floating-panel__empty">
        No metrics available
      </div>

      <!-- Tabbed Metrics Content -->
      <div v-else class="metrics-floating-panel__body">
        <a-tabs v-model:activeKey="activeTab" size="small">
          <!-- User Tab -->
          <a-tab-pane key="user" tab="User">
            <div
              v-for="item in userMetrics"
              :key="item.key"
              class="metrics-floating-panel__row"
              :data-testid="`user-metric-${item.key}`"
            >
              <span class="metrics-floating-panel__label">{{ item.label }}</span>
              <span class="metrics-floating-panel__value">{{ item.value }}</span>
            </div>
            <div
              v-for="n in userPadding"
              :key="`spacer-${n}`"
              class="metrics-floating-panel__row metrics-floating-panel__row--spacer"
              aria-hidden="true"
            >
              <span>&nbsp;</span>
            </div>
          </a-tab-pane>

          <!-- Tenant Tab -->
          <a-tab-pane key="tenant" tab="Tenant">
            <div
              v-for="item in tenantMetrics"
              :key="item.key"
              class="metrics-floating-panel__row"
              :data-testid="`tenant-metric-${item.key}`"
            >
              <span class="metrics-floating-panel__label">{{ item.label }}</span>
              <span class="metrics-floating-panel__value">{{ item.value }}</span>
            </div>
            <div
              v-for="n in tenantPadding"
              :key="`spacer-${n}`"
              class="metrics-floating-panel__row metrics-floating-panel__row--spacer"
              aria-hidden="true"
            >
              <span>&nbsp;</span>
            </div>
          </a-tab-pane>

          <!-- Subscription Tab (conditional) -->
          <a-tab-pane v-if="hasSubscription" key="subscription" tab="Subscription">
            <div
              v-for="item in subscriptionEntries"
              :key="item.key"
              class="metrics-floating-panel__row"
              :data-testid="`subscription-${item.key}`"
            >
              <span class="metrics-floating-panel__label">{{ item.label }}</span>
              <span class="metrics-floating-panel__value">{{ item.value }}</span>
            </div>
            <div
              v-for="n in subscriptionPadding"
              :key="`spacer-${n}`"
              class="metrics-floating-panel__row metrics-floating-panel__row--spacer"
              aria-hidden="true"
            >
              <span>&nbsp;</span>
            </div>
          </a-tab-pane>

          <!-- Diagnostics Tab (conditional) -->
          <a-tab-pane v-if="hasDiagnostics" key="diagnostics" tab="Diagnostics">
            <div
              v-for="[key, value] in diagnosticsEntries"
              :key="key"
              class="metrics-floating-panel__row"
              :data-testid="`diagnostics-${key}`"
            >
              <span class="metrics-floating-panel__label">{{ key }}</span>
              <span class="metrics-floating-panel__value">{{ value }}</span>
            </div>
            <div
              v-for="n in diagnosticsPadding"
              :key="`spacer-${n}`"
              class="metrics-floating-panel__row metrics-floating-panel__row--spacer"
              aria-hidden="true"
            >
              <span>&nbsp;</span>
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.metrics-floating-panel {
  position: fixed;
  bottom: 80px;
  right: 24px;
  width: 340px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
  background-color: var(--card, #ffffff);
  border: 1px solid var(--border, #d9d9d9);
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.metrics-floating-panel__header {
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--border, #f0f0f0);
  flex-shrink: 0;
}

.metrics-floating-panel__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.metrics-floating-panel__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: var(--foreground, #333333);
}

.metrics-floating-panel__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: var(--muted-foreground, #666666);
  transition: background-color 0.2s ease;
}

.metrics-floating-panel__close-btn:hover {
  background-color: var(--muted, #f5f5f5);
}

.metrics-floating-panel__close-btn:focus-visible {
  outline: 2px solid #1677ff;
  outline-offset: 2px;
}

.metrics-floating-panel__freshness {
  font-size: 11px;
  color: var(--muted-foreground, #999999);
  margin-top: 2px;
}

.metrics-floating-panel__error-banner {
  padding: 8px 16px;
  background-color: #fff2f0;
  border-bottom: 1px solid #ffccc7;
  font-size: 12px;
  color: #cf1322;
  flex-shrink: 0;
}

.metrics-floating-panel__body {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 12px;
}

.metrics-floating-panel__body :deep(.ant-tabs-nav) {
  margin-bottom: 8px;
}

.metrics-floating-panel__body :deep(.ant-tabs-tab) {
  padding: 6px 0;
  font-size: 12px;
}

:global(.dark) .metrics-floating-panel__body :deep(.ant-tabs-tab .ant-tabs-tab-btn) {
  color: var(--muted-foreground);
}

:global(.dark) .metrics-floating-panel__body :deep(.ant-tabs-tab-active .ant-tabs-tab-btn) {
  color: var(--foreground);
}

.metrics-floating-panel__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 13px;
  border-bottom: 1px solid var(--border, #fafafa);
}

.metrics-floating-panel__row:last-child {
  border-bottom: none;
}

.metrics-floating-panel__row--spacer {
  border-bottom: none;
  visibility: hidden;
}

.metrics-floating-panel__label {
  color: var(--muted-foreground, #666666);
}

.metrics-floating-panel__value {
  font-weight: 500;
  color: var(--foreground, #333333);
  font-variant-numeric: tabular-nums;
}

.metrics-floating-panel__empty {
  padding: 24px 16px;
  text-align: center;
  color: var(--muted-foreground, #999999);
  font-size: 13px;
}

.metrics-floating-panel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
}

.metrics-floating-panel__loading-text {
  font-size: 13px;
  color: var(--muted-foreground, #999999);
}

/* Transition */
.metrics-panel-enter-active,
.metrics-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.metrics-panel-enter-from,
.metrics-panel-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>
